import './ClientAuthorisationToDiscloseInfoFormScreen.scss';
import CardComponent from "../../../shared/components/card/CardComponent";
import React, {useCallback, useEffect, useRef, useState} from "react";
import {Field, FieldArray, FieldProps, Form, Formik, FormikHelpers} from "formik";
import FormikInputComponent from "../../../shared/components/form-controls/formik-input/FormikInputComponent";
import {ImageConfig, Misc, Patterns} from "../../../constants";
import FormikPhoneInputComponent from "../../../shared/components/formik-phone-input/FormikPhoneInputComponent";
import FormikDatePickerComponent
    from "../../../shared/components/form-controls/formik-date-picker/FormikDatePickerComponent";
import _ from "lodash";
import HorizontalLineComponent
    from "../../../shared/components/horizontal-line/horizontal-line/HorizontalLineComponent";
import FormikCheckBoxComponent from "../../../shared/components/form-controls/formik-check-box/FormikCheckBoxComponent";
import LinkComponent from "../../../shared/components/link/LinkComponent";
import FormikRadioButtonGroupComponent
    from "../../../shared/components/form-controls/formik-radio-button-group/FormikRadioButtonGroupComponent";
import ButtonComponent from "../../../shared/components/button/ButtonComponent";
import ErrorComponent from "../../../shared/components/error/ErrorComponent";
import ESignApprovalComponent from "../../../shared/components/e-sign-approval/ESignApprovalComponent";
import SignaturePopupComponent from "../../../shared/components/signature-popup/SignaturePopupComponent";
import ModalComponent from "../../../shared/components/modal/ModalComponent";
import {useDispatch, useSelector} from "react-redux";
import {IRootReducerState} from "../../../store/reducers";
import {getAuthCasesList} from "../../../store/actions/dashboard.action";
import {CommonService} from "../../../shared/services";
import moment from "moment-timezone";
import * as Yup from "yup";
import FormDebuggerComponent from "../../../shared/components/form-debugger/FormDebuggerComponent";
import LoaderComponent from '../../../shared/components/loader/LoaderComponent';
import StatusCardComponent from "../../../shared/components/status-card/StatusCardComponent";
import {useLocation, useNavigate, useParams} from "react-router-dom";
import BackComponent from "../../../shared/components/back/BackComponent";
import AttendanceAndCancellationPolicyComponent
    from "../../transaction/attendance-and-cancellation-policy/AttendanceAndCancellationPolicyComponent";
import FormikSelectDropdownComponent
    from "../../../shared/components/form-controls/formik-select-dropdown/FormikSelectDropdownComponent";

interface ClientAuthorisationToDiscloseInfoFormScreenProps {

}


const AuthorizationToDiscloseMedicalInformationInitialValues: any = {
    patient_name: '',
    patient_ssn: '',
    patient_phone: '',
    patient_dob: '',
    patient_address: {
        address_line: '',
        zip_code: '',
        state: '',
        city: '',
        country: ''
    },
    is_notice_of_privacy_acknowledged: false,
    comments: '',
    client_name: '',
    date: new Date(),
    signature: '',
    is_authorized_representative: false,
    agreed_to_terms: false,
    information_disclosed_to: [
        {
            name: '',
            phone: '',
            relationship: '',
        }
    ],
    case_details: []
}

const authorizationToDiscloseMedicalInformationValidationSchema = Yup.object({
    patient_name: Yup.string().required('Name is required'),
    patient_ssn: Yup.string().matches(/^\d{9}$/, 'SSN is not valid').nullable(),
    patient_phone: Yup.string().required('Phone is required'),
    patient_dob: Yup.string().required('Date is required'),
    date: Yup.string().nullable(),
    patient_address: Yup.object({
        address_line: Yup.string().required('Address is required'),
        state: Yup.string().required('State is required'),
        city: Yup.string().required('City is required'),
        country: Yup.string().required('Country is required'),
        zip_code: Yup.string().required('Zip Code is required'),
    }),
    agreed_to_disclose_information: Yup.boolean().required('This field is required'),

    information_disclosed_to: Yup.mixed().when('agreed_to_disclose_information', {
        is: true,  // Only apply validation when agreed_to_disclose_information is true
        then: () => Yup.array().of(
            Yup.object({
                name: Yup.string().required('Name is required'),
                phone: Yup.string().required('Phone number is required'),
                relationship: Yup.string().required('Relationship is required'),
            })
        ).min(1, 'At least one contact is required'),
        otherwise: () => Yup.mixed().notRequired(), // No validation when false
    }),
    agreed_to_terms: Yup.boolean().oneOf([true], 'Please acknowledge to the terms and condition'),
    is_notice_of_privacy_acknowledged: Yup.boolean().oneOf([true], 'Please acknowledge to the notice of privacy'),
    is_authorized_representative: Yup.boolean(),
    representative_name: Yup.string().nullable().when("is_authorized_representative", {
        is: true,
        then: Yup.string().nullable().required('Authorized Representative Name is required')
    }),
    signature: Yup.string().required()
})


const ClientAuthorisationToDiscloseInfoFormScreen = (props: ClientAuthorisationToDiscloseInfoFormScreenProps) => {
    const [authorizationToDiscloseMedicalInformationFormValues, setAuthorizationToDiscloseMedicalInformationFormValues] = useState<any>(_.cloneDeep(AuthorizationToDiscloseMedicalInformationInitialValues));
    const formikRef = useRef<any>(null);
    const dispatch = useDispatch();
    const [isSignaturePopupOpen, setIsSignaturePopupOpen] = useState<boolean>(false);
    const [isDiscloseDataLoading, setIsDiscloseDataLoading] = useState<boolean>(false);
    const [discloseData, setDiscloseData] = useState<any>({});
    const [caseData, setCaseData] = useState<any>({});
    const navigate = useNavigate();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const mode = searchParams.get("mode");
    const {clientBasicDetails} = useSelector((state: IRootReducerState) => state.client);
    const [isPolicyModalOpen, setIsPolicyModalOpen] = useState<boolean>(false);
    const {id,appointmentId} = useParams();


    const {
        relationshipList
    } = useSelector((state: IRootReducerState) => state.staticData);

    const onSubmit = useCallback((values: any, {setSubmitting, setErrors}: FormikHelpers<any>) => {
        setSubmitting(true);
        if (id) {
            const payload = {
                ...values,
                mode: mode,
                appointment_id: discloseData?.appointment_id,
            }
            CommonService._dashboard.EditAuthorisationToDiscloseInfo(id, payload)
                .then((response) => {
                    setSubmitting(false);
                    navigate(CommonService._routeConfig.ClientResource())
                    CommonService._alert.showToast(response[Misc.API_RESPONSE_MESSAGE_KEY], "success");

                }).catch((error) => {
                setSubmitting(false);
                setErrors(error.response.data);
                CommonService._alert.showToast(error.response.data[Misc.API_RESPONSE_MESSAGE_KEY], 'error');
            })
        }

    }, [mode,id,discloseData, navigate]);

    useEffect(() => {
        console.log('caseData', caseData)
    }, [caseData]);

    useEffect(() => {
        dispatch(getAuthCasesList());
    }, [dispatch]);

    const getDisclosedData = useCallback(() => {
        setIsDiscloseDataLoading(true);

        if (!appointmentId) return;
        CommonService._dashboard.GetAuthorisationToDiscloseInfo(appointmentId)
            .then((response) => {
                setIsDiscloseDataLoading(false);
                setDiscloseData(response?.data || {});
            })
            .catch((error) => {
                setIsDiscloseDataLoading(false);
                console.error('Error fetching data:', error);
                setDiscloseData({});
            });
    }, [appointmentId]);

    useEffect(() => {
        if (discloseData?.record_type === 'performance_record') {
            const performanceRecorded = CommonService.getDateFromTimezone(discloseData?.case_details?.created_at, moment.tz.guess(), 'DD-MMM-YYYY') + '-' + discloseData?.program_focus;
            setCaseData(performanceRecorded)
        } else {
            const medicalData = CommonService.getDateFromTimezone(discloseData?.case_details?.created_at, moment.tz.guess(), 'DD-MMM-YYYY') + '-' + discloseData?.case_details?.injury_details?.map((injury: any) => {
                return injury?.body_side + '-' + injury?.body_part_name;
            }).join(', ');
            setCaseData(medicalData)
        }

    }, [discloseData]);

    useEffect(() => {
        getDisclosedData();
    }, [getDisclosedData]);


    const handleSignaturePopupClose = useCallback(() => {
        setIsSignaturePopupOpen(false);
    }, []);

    useEffect(() => {
        if (discloseData) {
            const defaultDisclosureItem = {
                name: '',
                phone: '',
                relationship: ''
            };

            // Initialize with at least one item if agreed_to_disclose_information is true
            const disclosureArray =
                (discloseData?.information_disclosed_to?.length ? discloseData.information_disclosed_to : [defaultDisclosureItem])
                ;
            setAuthorizationToDiscloseMedicalInformationFormValues((prevState: any) => ({
                ...prevState,
                patient_name: discloseData?.patient_name,
                patient_ssn: discloseData?.patient_ssn || '',
                patient_phone: discloseData?.patient_phone || '',
                patient_dob: discloseData?.patient_dob || '',
                patient_address: {
                    address_line: discloseData?.patient_address?.address_line || '',
                    zip_code: discloseData?.patient_address?.zip_code || '',
                    state: discloseData?.patient_address?.state || '',
                    city: discloseData?.patient_address?.city || '',
                    country: discloseData?.patient_address?.country || '',
                },
                client_name: discloseData?.client_name || clientBasicDetails?.first_name + " " + clientBasicDetails?.last_name,
                is_notice_of_privacy_acknowledged: discloseData?.is_notice_of_privacy_acknowledged,
                agreed_to_disclose_information: discloseData?.agreed_to_disclose_information,
                information_disclosed_to: disclosureArray,
                is_authorized_representative: discloseData?.is_authorized_representative,
                representative_name: discloseData?.representative_name,
                agreed_to_terms: discloseData?.agreed_to_terms,

            }));
        }
    }, [discloseData, clientBasicDetails]);


    return (
        <div className={'client-authorisation-to-disclose-info-form-screen'}>
            <div className="list-screen__header">
                <div className="list-screen__header-title">
                    <BackComponent onClick={() => navigate(CommonService._routeConfig.ClientResource())}/>
                </div>
            </div>
            {
                isDiscloseDataLoading && <LoaderComponent/>
            }
            {
                !isDiscloseDataLoading && !discloseData && <StatusCardComponent title={'Failed to get data'}/>
            }
            {
                (!isDiscloseDataLoading && discloseData) &&

                <CardComponent>
                    <div className={'client-authorisation-to-disclose-info-form-header'}>
                        Authorization to Disclose Protected Health Information
                    </div>
                    <div className={'client-authorisation-to-disclose-info-form-body'}>
                        This form must be fully completed in order to process your request. Your Protected Health
                        Information
                        (PHI) will only be disclosed by Kinergy Sports Medicine and Performance (hereafter known as
                        KINERGY)
                        to
                        the person/entity listed below and will not be shared with any other individuals without your
                        consent,
                        unless required or permitted by law or as outlined in our Notice of Privacy Practices.
                    </div>
                    <Formik initialValues={authorizationToDiscloseMedicalInformationFormValues}
                            validateOnChange={false}
                            validateOnBlur={true}
                            innerRef={formikRef}
                            validationSchema={authorizationToDiscloseMedicalInformationValidationSchema}
                            enableReinitialize={true}
                            validateOnMount={true}
                            onSubmit={onSubmit}>
                        {({
                              values,
                              touched,
                              errors,
                              setFieldValue,
                              validateForm,
                              isValid,
                              isSubmitting,
                              dirty
                          }) => {
                            // eslint-disable-next-line react-hooks/rules-of-hooks
                            useEffect(() => {
                                validateForm();
                            }, [validateForm, values]);
                            return (
                                <Form className={'t-form'} noValidate={true}>
                                    <FormDebuggerComponent values={values} errors={errors} showDebugger={true}/>
                                    <div className={'t-form-controls'}>
                                        <>
                                            <div
                                                className={'client-authorisation-to-disclose-info-form-client-details'}>Part
                                                1: Client Information
                                            </div>
                                            <div className={'ts-row'}>
                                                <div className={'ts-col-lg-6'}>
                                                    <Field name={'patient_name'} className="t-form-control">
                                                        {
                                                            (field: FieldProps) => (
                                                                <FormikInputComponent
                                                                    id={"full_name_input"}
                                                                    label={'Name of Client'}
                                                                    placeholder={'Enter Full Name'}
                                                                    required={true}
                                                                    titleCase={true}
                                                                    formikField={field}
                                                                    fullWidth={true}
                                                                />
                                                            )
                                                        }
                                                    </Field>
                                                </div>
                                                <div className={'ts-col-lg-6'}>
                                                    <Field name={'patient_ssn'} className="t-form-control">
                                                        {
                                                            (field: FieldProps) => (
                                                                <FormikInputComponent
                                                                    id={"social_security_input"}
                                                                    label={'Social Security Number'}
                                                                    required={false}
                                                                    type={'text'}
                                                                    validationPattern={Patterns.NINE_DIGITS_ONLY}
                                                                    titleCase={true}
                                                                    formikField={field}
                                                                    fullWidth={true}
                                                                />
                                                            )
                                                        }
                                                    </Field>
                                                </div>
                                            </div>
                                            <div className={'ts-row'}>
                                                <div className={'ts-col-lg-6'}>
                                                    <Field name={'patient_phone'} className="t-form-control">
                                                        {
                                                            (field: FieldProps) => (
                                                                <FormikPhoneInputComponent
                                                                    id={"phone_number_input"}
                                                                    label={'Phone Number'}
                                                                    required={true}
                                                                    formikField={field}
                                                                    fullWidth={true}
                                                                />
                                                            )
                                                        }
                                                    </Field>
                                                </div>
                                                <div className={'ts-col-lg-6'}>
                                                    <Field name={'patient_dob'} className="t-form-control">
                                                        {
                                                            (field: FieldProps) => (
                                                                <FormikDatePickerComponent
                                                                    id={"dob_input"}
                                                                    label={'Date of Birth'}
                                                                    placeholder={'Enter your date of birth'}
                                                                    required={true}
                                                                    formikField={field}
                                                                    fullWidth={true}
                                                                />
                                                            )
                                                        }
                                                    </Field>
                                                </div>
                                            </div>
                                            <div className={'ts-row'}>
                                                <div className={'ts-col-lg-12'}>
                                                    <Field name={'patient_address.address_line'}
                                                           className="t-form-control">
                                                        {
                                                            (field: FieldProps) => (
                                                                <FormikInputComponent
                                                                    id={"address_input"}
                                                                    label={'Address'}
                                                                    placeholder={'Enter your Address'}
                                                                    formikField={field}
                                                                    required={true}
                                                                    fullWidth={true}
                                                                />
                                                            )
                                                        }
                                                    </Field>
                                                </div>
                                            </div>
                                            <div className={'ts-row'}>
                                                <div className={'ts-col-lg-6'}>
                                                    <Field name={'patient_address.city'} className="t-form-control">
                                                        {
                                                            (field: FieldProps) => (
                                                                <FormikInputComponent
                                                                    id={"city_input"}
                                                                    label={'City'}
                                                                    placeholder={'Enter your City'}
                                                                    formikField={field}
                                                                    required={true}
                                                                    fullWidth={true}
                                                                />
                                                            )
                                                        }
                                                    </Field>
                                                </div>
                                                <div className={'ts-col-lg-6'}>
                                                    <Field name={'patient_address.state'}
                                                           className="t-form-control">
                                                        {
                                                            (field: FieldProps) => (
                                                                <FormikInputComponent
                                                                    id={"state_input"}
                                                                    label={'State'}
                                                                    placeholder={'Enter your State'}
                                                                    formikField={field}
                                                                    required={true}
                                                                    fullWidth={true}
                                                                />
                                                            )
                                                        }
                                                    </Field>
                                                </div>

                                            </div>
                                            <div className={'ts-row'}>
                                                <div className={'ts-col-lg-6'}>
                                                    <Field name={'patient_address.zip_code'}
                                                           className="t-form-control">
                                                        {
                                                            (field: FieldProps) => (
                                                                <FormikInputComponent
                                                                    id={"zip_input"}
                                                                    label={'ZIP Code'}
                                                                    validationPattern={Patterns.FIVE_DIGITS_ONLY}
                                                                    placeholder={'Enter your ZIP Code'}
                                                                    formikField={field}
                                                                    required={true}
                                                                    fullWidth={true}
                                                                />
                                                            )
                                                        }
                                                    </Field>
                                                </div>
                                                <div className={'ts-col-lg-6'}>
                                                    <Field name={'patient_address.country'}
                                                           className="t-form-control">
                                                        {
                                                            (field: FieldProps) => (
                                                                <FormikInputComponent
                                                                    id={"country_input"}
                                                                    label={'Country'}
                                                                    placeholder={'Enter your Country'}
                                                                    formikField={field}
                                                                    required={true}
                                                                    fullWidth={true}
                                                                />
                                                            )
                                                        }
                                                    </Field>
                                                </div>
                                            </div>
                                            <HorizontalLineComponent/>
                                            <div className={'notice-wrapper'}>
                                                <div className={'notice-wrapper-header'}>Part 2: Notice of Privacy
                                                    Practices
                                                </div>
                                                <LinkComponent onClick={() => setIsPolicyModalOpen(true)}>View
                                                    Notice</LinkComponent>
                                            </div>
                                            <div className={'ts-row'}>
                                                <div className={'ts-col-lg-12'}>
                                                    <Field name={'is_notice_of_privacy_acknowledged'}
                                                           className="t-form-control">
                                                        {
                                                            (field: FieldProps) => (
                                                                <FormikCheckBoxComponent
                                                                    id={"country_input"}
                                                                    label={'I acknowledge that I have been granted access to and had the opportunity to review KINERGY\'s Notice of Privacy Practices, which explains how my protected health information may be used and disclosed for treatment, training, payment, healthcare operations, and other permitted purposes. I understand that this notice is available to me via my Client Portal or can be viewed online at any time. I understand that if I have any questions or concerns, I can contact the Privacy Officer listed in the notice. Additionally, I understand that my information may be disclosed electronically by KINERGY and/or its staff. To the extent permitted by law, I consent to the use and disclosure of my protected health information as outlined in KINERGY\'s Notice of Privacy Practices.'}
                                                                    formikField={field}
                                                                    color={'accent'}
                                                                    required={true}
                                                                />
                                                            )
                                                        }
                                                    </Field>
                                                </div>
                                            </div>
                                            <HorizontalLineComponent/>
                                            <div className={'notice-wrapper'}>
                                                <div className={'notice-wrapper-header'}>Part 3: Disclosures to Friends
                                                    and/or Family Members
                                                </div>
                                            </div>
                                            <div className={'disclose-details'}>Would you like to designate an
                                                individual or
                                                entity with whom KINERGY's
                                                staff may discuss your Protected Health Information?
                                            </div>
                                            <div className={'ts-row'}>
                                                <div className={'ts-col-lg-12'}>
                                                    <Field name={'agreed_to_disclose_information'}
                                                           className="t-form-control">
                                                        {
                                                            (field: FieldProps) => (
                                                                <FormikRadioButtonGroupComponent
                                                                    direction={'row'}
                                                                    options={CommonService._staticData.options}
                                                                    displayWith={(option) => option.label}
                                                                    valueExtractor={(option) => option.value}
                                                                    formikField={field}
                                                                    required={true}
                                                                />
                                                            )
                                                        }
                                                    </Field>
                                                </div>
                                            </div>
                                            <div className={'select-cases'}>
                                                Select the case(s) for which this disclosure applies*
                                            </div>
                                            {
                                                (mode === 'edit' && discloseData?.case_details) && <>
                                                    <div className="disclose-medical-record-header">
                                                        {discloseData?.record_type === 'medical_record' ?
                                                            <span className="medical-info">
                                                            { CommonService.getDateFromTimezone(discloseData?.created_at, moment.tz.guess(), 'DD-MMM-YYYY')} - {
                                                                Array.from(new Set(
                                                                    discloseData?.case_details?.injury_details
                                                                        ?.map((injury: any) => `${injury.body_part_name} (${injury.body_side})`)
                                                                )).join(' | ')
                                                            }
                                                            </span> : <span
                                                                className="medical-info">{<>{CommonService.getDateFromTimezone(discloseData?.created_at, moment.tz.guess(), 'DD-MMM-YYYY')} - {discloseData?.case_details?.program_focus}</>}</span>}
                                                        <div className="dropdown-icon">
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="16"
                                                                 height="16" viewBox="0 0 24 24" fill="none"
                                                                 stroke="currentColor" stroke-width="2"
                                                                 stroke-linecap="round" stroke-linejoin="round">
                                                                <polyline points="6 9 12 15 18 9"></polyline>
                                                            </svg>
                                                        </div>
                                                    </div>
                                                </>
                                            }
                                            <div className={'disclose-info-details'}>You may disclose this information
                                                to:
                                            </div>
                                            <FieldArray name={'information_disclosed_to'}
                                                        render={(arrayHelpers) => (
                                                            <>
                                                                {values.information_disclosed_to && values?.information_disclosed_to?.map((item: any, index: any) => {
                                                                    return (
                                                                        <div key={index}>
                                                                            {values?.information_disclosed_to?.length > 1 &&
                                                                                <div
                                                                                    className={'contact-title-and-remove-button-wrapper'}>
                                                                                    <div>Contact: {index + 1}</div>
                                                                                    <ButtonComponent
                                                                                        className={'mrg-left-10'}
                                                                                        color={'error'}
                                                                                        variant={'outlined'}
                                                                                        size={'small'}
                                                                                        prefixIcon={
                                                                                            <ImageConfig.DeleteIcon/>}
                                                                                        onClick={() => {
                                                                                            arrayHelpers.remove(index)
                                                                                        }}
                                                                                    >
                                                                                        Remove
                                                                                    </ButtonComponent>
                                                                                </div>
                                                                            }
                                                                            <div className={'ts-row'}>
                                                                                <div className={'ts-col-lg-6'}>
                                                                                    <Field
                                                                                        name={`information_disclosed_to[${index}].name`}>
                                                                                        {
                                                                                            (field: FieldProps) => (
                                                                                                <FormikInputComponent
                                                                                                    formikField={field}
                                                                                                    label={'Full Name'}
                                                                                                    titleCase={true}
                                                                                                    fullWidth={true}
                                                                                                    required={true}
                                                                                                />
                                                                                            )
                                                                                        }
                                                                                    </Field>
                                                                                </div>
                                                                                <div className={'ts-col-lg-6'}>
                                                                                    <Field
                                                                                        name={`information_disclosed_to[${index}].relationship`}>
                                                                                        {
                                                                                            (field: FieldProps) => (
                                                                                                <FormikSelectDropdownComponent
                                                                                                    id={'relationship_dd'}
                                                                                                    options={relationshipList}
                                                                                                    label={'Relationship'}
                                                                                                    placeholder={'Select Relationship'}
                                                                                                    required={true}
                                                                                                    formikField={field}
                                                                                                    fullWidth={true}
                                                                                                    isClearable={false}
                                                                                                />
                                                                                            )
                                                                                        }
                                                                                    </Field>
                                                                                </div>
                                                                            </div>
                                                                            <div className={'ts-row'}>
                                                                                <div className={'ts-col-lg-6'}>
                                                                                    <Field
                                                                                        name={`information_disclosed_to[${index}].phone`}>
                                                                                        {
                                                                                            (field: FieldProps) => (
                                                                                                <FormikPhoneInputComponent
                                                                                                    formikField={field}
                                                                                                    label={'Phone Number'}
                                                                                                    fullWidth={true}
                                                                                                    required={true}
                                                                                                />
                                                                                            )
                                                                                        }
                                                                                    </Field>
                                                                                </div>
                                                                            </div>
                                                                            <div className={'ts-row'}>
                                                                                {
                                                                                    values?.information_disclosed_to?.length > 1 &&
                                                                                    <HorizontalLineComponent/>
                                                                                }
                                                                                {
                                                                                    (index === values?.information_disclosed_to?.length - 1) && values.information_disclosed_to.length < 5 &&
                                                                                    <ButtonComponent
                                                                                        className={'mrg-bottom-30'}
                                                                                        size={'small'}
                                                                                        prefixIcon={
                                                                                            <ImageConfig.AddIcon/>}
                                                                                        onClick={() => {
                                                                                            arrayHelpers.push({
                                                                                                name: '',
                                                                                                phone_number: '',
                                                                                                relationship: '',

                                                                                            })
                                                                                        }}
                                                                                    >
                                                                                        Add Another Contact
                                                                                    </ButtonComponent>
                                                                                }
                                                                            </div>

                                                                        </div>
                                                                    )
                                                                })}
                                                            </>
                                                        )}/>
                                            <div className={'purpose-of-disclosure'}>Purpose of Disclosure:</div>
                                            <div className={'purpose-of-disclosure-description'}>
                                                I understand that the purpose of this authorization is to provide
                                                relevant
                                                pieces of my Protected Health Information (PHI) to the individual(s)
                                                listed
                                                above, to facilitate communication, coordination, and continuity of
                                                care. I
                                                acknowledge that my information may not be disclosed to any other
                                                individual
                                                or entity by KINERGY without my expressed consent, unless required or
                                                permitted by law or as outlined in our Notice of Privacy Practices.
                                            </div>
                                            <div className={'purpose-of-disclosure'}>
                                                Expiration Date of Authorization:
                                            </div>
                                            <div className={'purpose-of-disclosure-description'}>
                                                This authorization will remain valid for one (1) year from the date of
                                                signing.
                                            </div>
                                            <HorizontalLineComponent/>
                                            <div className={'purpose-of-disclosure rights'}>
                                                Part 4: Client Acknowledgment and Rights
                                            </div>
                                            <div className={'purpose-of-disclosure-description mrg-left-20'}>
                                                <ul>
                                                    <li className={'mrg-bottom-20'}>
                                                        I acknowledge that this authorization is voluntary.
                                                    </li>
                                                    <li className={'mrg-bottom-20'}>
                                                        I understand that by granting this authorization, the recipient
                                                        of
                                                        my protected health information (PHI) may not be subject to the
                                                        same
                                                        privacy protections under HIPAA, and they may disclose this
                                                        information to others, with or without my consent. Once my
                                                        information is disclosed by KINERGY, it may no longer be
                                                        protected
                                                        by HIPAA or other privacy laws.
                                                    </li>
                                                    <li className={'mrg-bottom-20'}>
                                                        I understand that I may revoke this authorization at any time
                                                        prior
                                                        to its expiration by submitting written notice of revocation to
                                                        the
                                                        entity providing the information. I understand I cannot revoke
                                                        this
                                                        authorization retroactively for information already released.
                                                    </li>
                                                    <li className={'mrg-bottom-20'}>
                                                        I understand that I have the right to access and copy the
                                                        information described on this form upon request.
                                                    </li>
                                                </ul>
                                            </div>
                                            <div className="ts-row pdd-top-30">
                                                <div className="ts-col-12">
                                                    <Field name={'agreed_to_terms'}
                                                           className="t-form-control">
                                                        {
                                                            (field: FieldProps) => (
                                                                <FormikCheckBoxComponent
                                                                    label={'I certify that I have read and fully understand the above statements and consent fully and voluntarily to its contents.'}
                                                                    formikField={field}

                                                                />
                                                            )
                                                        }
                                                    </Field>
                                                </div>
                                            </div>
                                            <div className="ts-row">
                                                <div className="ts-col-12">
                                                    <Field name={'is_authorized_representative'}
                                                           className="t-form-control">
                                                        {
                                                            (field: FieldProps) => (
                                                                <FormikCheckBoxComponent
                                                                    label={'I am an Authorized Representative for the client (ie. Parent, Legal Guardian, Agent, Manager, etc.)'}
                                                                    formikField={field}
                                                                    onChange={() => {
                                                                        setFieldValue('representative_name', '');
                                                                    }}
                                                                />
                                                            )
                                                        }
                                                    </Field>
                                                </div>
                                            </div>
                                            <div className="ts-row mrg-top-20">
                                                <div className="ts-col-md-12 ts-col-lg-6">
                                                    <Field name={'client_name'} className="t-form-control">
                                                        {
                                                            (field: FieldProps) => (
                                                                <FormikInputComponent
                                                                    id={"name_input"}
                                                                    label={"Client's Name"}
                                                                    placeholder={"Enter Client's Name"}
                                                                    required={true}
                                                                    disabled={true}
                                                                    formikField={field}
                                                                    fullWidth={true}
                                                                    titleCase={true}
                                                                />
                                                            )
                                                        }
                                                    </Field>
                                                </div>
                                                <div className="ts-col-md-12 ts-col-lg-6">
                                                    <Field name={'date'} className="t-form-control">
                                                        {
                                                            (field: FieldProps) => (
                                                                <FormikDatePickerComponent
                                                                    id={"dob_input"}
                                                                    label={'Date'}
                                                                    placeholder={'Enter Date'}
                                                                    formikField={field}
                                                                    fullWidth={true}
                                                                    disabled={true}
                                                                    readOnly={true}
                                                                />
                                                            )
                                                        }
                                                    </Field>
                                                </div>
                                            </div>
                                            {
                                                values.is_authorized_representative && <div className="ts-row">
                                                    <div className="ts-col-6">
                                                        <Field name={'representative_name'} className="t-form-control">
                                                            {
                                                                (field: FieldProps) => (
                                                                    <FormikInputComponent
                                                                        label={'Authorized Representative Name'}
                                                                        placeholder={'Enter Representative Name'}
                                                                        required={true}
                                                                        fullWidth={true}
                                                                        formikField={field}
                                                                        titleCase={true}
                                                                    />
                                                                )
                                                            }
                                                        </Field>
                                                    </div>
                                                </div>
                                            }
                                            {!(values?.signature && values?.signature) &&
                                                <div className="ts-row">
                                                    <div className="ts-col-12">
                                                        <div
                                                            className={"signature-label"}
                                                        >Signature of Client or Authorized Representative
                                                        </div>
                                                        <ButtonComponent
                                                            variant={'outlined'}
                                                            onClick={() => {
                                                                setIsSignaturePopupOpen(true);
                                                            }}
                                                            color={'accent'}
                                                        >
                                                            Add Signature
                                                        </ButtonComponent>
                                                    </div>
                                                    {
                                                        (_.get(touched, "signature") && !!(_.get(errors, "signature"))) &&
                                                        <ErrorComponent
                                                            errorText={(_.get(errors, "signature"))}/>
                                                    }
                                                </div>
                                            }
                                            {(values?.signature) &&
                                                <div className={'ts-col-lg-6'}>
                                                    <ESignApprovalComponent isSigned={true}
                                                                            signature_url={values?.signature}
                                                    />
                                                    <div className={'signature-remove-button'}>
                                                        <LinkComponent
                                                            onClick={() => {
                                                                setFieldValue('signature', ''); // Clear only the signature field
                                                            }}
                                                        >
                                                            Remove Signature
                                                        </LinkComponent>
                                                    </div>
                                                </div>
                                            }
                                        </>
                                        <div className="t-form-actions">
                                            <ButtonComponent className={'mrg-right-10'} variant={'outlined'}
                                                             onClick={() => navigate(CommonService._routeConfig.ClientResource())}
                                                             color={'accent'}>
                                                Cancel
                                            </ButtonComponent>
                                            <ButtonComponent
                                                id={"submit_btn"}
                                                type={"submit"}
                                                color={'accent'}
                                                disabled={!isValid || isSubmitting}
                                                isLoading={isSubmitting}
                                            >
                                                Save
                                            </ButtonComponent>
                                        </div>
                                    </div>
                                </Form>
                            )
                        }}
                    </Formik>
                    <ModalComponent isOpen={isSignaturePopupOpen}
                                    onClose={handleSignaturePopupClose}
                                    size={"xl"}
                    >
                        <SignaturePopupComponent
                            onSign={(signImage: any) => {
                                formikRef?.current.setFieldValue('signature', signImage)
                                setAuthorizationToDiscloseMedicalInformationFormValues({
                                    ...formikRef?.current?.values,
                                    signature: signImage
                                })
                                handleSignaturePopupClose();
                            }}
                            handleSignaturePopupClose={handleSignaturePopupClose}
                        />
                    </ModalComponent>
                    <ModalComponent isOpen={isPolicyModalOpen}>
                        <AttendanceAndCancellationPolicyComponent isNoticeOfPrivacyPolicyNeeded={true}
                                                                  setIsAttendanceModalOpen={setIsPolicyModalOpen}/>
                    </ModalComponent>
                </CardComponent>
            }
        </div>
    )
}

export default ClientAuthorisationToDiscloseInfoFormScreen
